import { FC, useEffect, useRef, useState } from 'react';
import { Button, Modal, Form, message } from 'antd';
import { yupSync } from '../../utils';
import { FloatInput } from '../Fields/FloatInput';

import close from '../../assets/RoundedClose.svg';

import * as Yup from 'yup';

import './Modals.scss';
import { useAddSupplierMutation, useUpdateSupplierMutation } from '../../api/tenders';
import { SupplierI, tenderDataProps } from '../../pages/TenderViewPage/types';
import { Loader } from '../Loader/Loader';
import { CustomErrorType } from '../../api/types/global';
import { useAppSelector } from '../../hooks';

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenAddSuppInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
    SupplierData: SupplierI | null;
    data: tenderDataProps;
    /** Called after the modal finishes closing; use to reset edit state (e.g. clear SupplierData in parent). */
    onAfterClose?: () => void;
}

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required('This is a required field')
        .test('not-only-spaces', 'This is a required field', (value) => {
            return value.trim() !== '';
        }),
    email: Yup.string().email('Enter your email: e.g. email@domain.com').required('Email field is required')
});

interface SupplierData {
    name: string;
    email: string;
    company_name: string;
}

interface DataI {
    tenderId: number;
    supplierId?: number;
    data: SupplierData;
}

export const AddSupplierModal: FC<ModalProps> = ({
    openModal,
    setOpenModal,
    setTrigger,
    SupplierData,
    data,
    setOpenAddSuppInfoModal,
    onAfterClose
}) => {
    const [form] = Form.useForm();
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [changed, setChanged] = useState(false);
    const [alreadyInUse, setAlreadyInUse] = useState(false);
    /** When the main modal closes to show the duplicate-email dialog, parent `onAfterClose` must not run yet (would unmount this component in SupplierListNew). */
    const skipAfterCloseForDuplicateRef = useRef(false);
    const role = useAppSelector((state) => state.profileReducer.role);

    const onInput = () => {
        setChanged(true);
    };

    const [addSupplier, { data: supplierData, isLoading, isSuccess, isError, error }] = useAddSupplierMutation();
    const [
        updateSupplier,
        {
            data: updatedData,
            isLoading: updateIsLoading,
            isSuccess: updateIsSuccess,
            isError: isErrorUpdateSupplier,
            error: errorUpdateSupplier
        }
    ] = useUpdateSupplierMutation();

    useEffect(() => {
        if (isErrorUpdateSupplier) {
            if (
                (errorUpdateSupplier as CustomErrorType)?.data?.message ===
                'Oops! It looks like that email is already in use'
            ) {
                skipAfterCloseForDuplicateRef.current = true;
                setAlreadyInUse(true);
                setOpenModal(false);
            }
        }
        if (isError) {
            if ((error as CustomErrorType)?.data?.message === 'Oops! It looks like that email is already in use') {
                skipAfterCloseForDuplicateRef.current = true;
                setAlreadyInUse(true);
                setOpenModal(false);
            }
        }
    }, [isErrorUpdateSupplier, isError, error, errorUpdateSupplier, setOpenModal]);

    useEffect(() => {
        if (!openModal) return;
        if (SupplierData) {
            form.setFieldsValue({
                name: SupplierData.company_name,
                email: SupplierData.email,
                company_name: SupplierData.company_name
            });
        } else {
            form.resetFields();
        }
        setChanged(false);
    }, [openModal, SupplierData, form]);

    useEffect(() => {
        if (isSuccess) {
            setTrigger((prev: boolean) => !prev);
            message?.success('Supplier has been added successfully');
            setOpenModal(false);
            setOpenAddSuppInfoModal(false);
        }
        if (updateIsSuccess) {
            setTrigger((prev: boolean) => !prev);
            setOpenModal(false);
            message?.success(updatedData.message);
        }
    }, [isSuccess, updateIsSuccess]);

    const handleFormSubmit = () => {
        form.validateFields()
            .then((values: any) => {
                const dataToPost: DataI = {
                    tenderId: data.id,
                    data: {
                        name: values.name.trim(),
                        email: values.email.trim().toLowerCase(),
                        company_name: values.name.trim()
                    }
                };

                if (SupplierData?.id || role === 'onboarding_representative') {
                    dataToPost.supplierId = SupplierData?.id;
                    updateSupplier(dataToPost);
                } else {
                    addSupplier(dataToPost);
                }
            })
            .catch((err) => console.log('err', err));
    };

    return (
        <>
            <Modal
                destroyOnClose
                className={`modal`}
                centered
                open={openModal}
                afterClose={() => {
                    if (skipAfterCloseForDuplicateRef.current) {
                        skipAfterCloseForDuplicateRef.current = false;
                        return;
                    }
                    onAfterClose?.();
                }}
                onCancel={() => {
                    if (changed) {
                        setIsConfirmModal(true);
                    } else {
                        setOpenModal(false);
                    }
                }}
                closeIcon={<img src={close} />}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button
                            block
                            key="back"
                            onClick={() => {
                                if (changed) {
                                    setIsConfirmModal(true);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="cancel"
                        >
                            Cancel
                        </Button>
                        <Button form="schoolForm" type="primary" block onClick={handleFormSubmit}>
                            {isLoading || updateIsLoading ? <Loader /> : SupplierData ? 'Save' : 'Add'}
                        </Button>
                    </div>
                }
            >
                <div className="modal">
                    <div className="modal__header meeting" style={{ height: '88px' }}>
                        <div className="modal__title">{SupplierData ? 'Edit Supplier' : 'Add a New Supplier'}</div>
                    </div>
                    <div className="modal__main main" style={{ padding: '15px 56px 43px 56px' }}>
                        <Form form={form}>
                            <Form.Item
                                className="input-wrapper"
                                name="name"
                                rules={yupSync('name', validationSchema, true)}
                            >
                                <FloatInput
                                    floatLabel="Company Name"
                                    className="input"
                                    required
                                    maxLength={60}
                                    onInput={onInput}
                                />
                            </Form.Item>

                            <Form.Item
                                className="input-wrapper"
                                name="email"
                                rules={yupSync('email', validationSchema, true)}
                            >
                                <FloatInput
                                    floatLabel="Supplier email"
                                    className="input"
                                    maxLength={60}
                                    required
                                    onInput={onInput}
                                />
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal
                className={`confirmModal`}
                centered
                open={isConfirmModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsConfirmModal(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={() => {
                                setOpenModal(false);
                                setIsConfirmModal(false);
                            }}
                        >
                            Quit
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">Your data won’t be saved! Are you sure you want to quit?</div>
            </Modal>
            <Modal
                className={`confirmModal`}
                centered
                open={alreadyInUse}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ width: '100%', margin: '12px 0 -10px 0' }}>
                        <Button
                            type="primary"
                            onClick={() => {
                                setAlreadyInUse(false);
                                onAfterClose?.();
                            }}
                            style={{ width: '100%' }}
                        >
                            Got it
                        </Button>
                    </div>
                }
            >
                <div className="modal">
                    <div className="modal__header meeting" style={{ height: '88px' }}>
                        <div className="modal__title">Supplier Already on Proco</div>
                    </div>
                    <div className="modal__main main" style={{ padding: '15px 56px 43px 56px', fontWeight: 500 }}>
                        We recognised the supplier from their email domain, and they are already registered on Proco.
                        <br />
                        They’ll be notified of this service opportunity once you’ve completed all tasks in this phase.
                    </div>
                </div>
            </Modal>
        </>
    );
};
